import { Cancel01Icon } from '@hugeicons/core-free-icons';
import { Icon } from './icon.js';
import { Dialog as DialogPrimitive } from 'radix-ui';
import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { Button } from './button.js';
import { FormField } from './form-field.js';
import { Input } from './input.js';

export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;

/** Remembers the element focused when a controlled overlay opened so focus can return to it on close. */
function useReturnFocus(open: boolean | undefined) {
  const ref = React.useRef<HTMLElement | null>(null);
  React.useLayoutEffect(() => {
    if (open && !ref.current && typeof document !== 'undefined') ref.current = document.activeElement as HTMLElement | null;
  }, [open]);
  return React.useCallback((e: Event) => {
    const el = ref.current;
    ref.current = null;
    if (el && el.isConnected && el !== document.body) {
      e.preventDefault();
      el.focus();
    }
  }, []);
}

const closeButton =
  'inline-flex size-9 shrink-0 items-center justify-center rounded-control text-text-secondary hover:bg-surface-subtle hover:text-text-primary focus-visible:outline-2 focus-visible:outline-focus';

export interface DialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  /** Element that opens the dialog (optional for controlled dialogs). */
  trigger?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
  /** When false, outside click and Escape do not close (e.g. while submitting). */
  dismissible?: boolean;
  className?: string;
}

const DIALOG_WIDTH = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl' } as const;

/** Modal dialog: focus is trapped inside, Escape closes and focus returns to the opener. */
export function Dialog({ open, onOpenChange, title, description, children, footer, trigger, size = 'md', dismissible = true, className }: DialogProps) {
  const onCloseAutoFocus = useReturnFocus(trigger ? undefined : open);
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      {trigger ? <DialogPrimitive.Trigger asChild>{trigger}</DialogPrimitive.Trigger> : null}
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-surface-overlay animate-fade-in" />
        <DialogPrimitive.Content
          onCloseAutoFocus={trigger ? undefined : onCloseAutoFocus}
          onPointerDownOutside={dismissible ? undefined : (e) => e.preventDefault()}
          onEscapeKeyDown={dismissible ? undefined : (e) => e.preventDefault()}
          className={cn(
            'fixed left-1/2 top-1/2 z-50 grid max-h-[calc(100dvh-32px)] w-[calc(100vw-32px)] -translate-x-1/2 -translate-y-1/2 grid-rows-[auto_1fr_auto]',
            'rounded-dialog border border-border-subtle bg-surface-default shadow-overlay animate-fade-in',
            DIALOG_WIDTH[size],
            className,
          )}
        >
          <div className="flex items-start justify-between gap-4 px-6 pb-2 pt-5">
            <div className="grid gap-1">
              <DialogPrimitive.Title className="text-section font-semibold text-text-primary">{title}</DialogPrimitive.Title>
              {description ? (
                <DialogPrimitive.Description className="text-body text-text-secondary">{description}</DialogPrimitive.Description>
              ) : (
                <DialogPrimitive.Description className="sr-only">{typeof title === 'string' ? title : 'Diálogo'}</DialogPrimitive.Description>
              )}
            </div>
            <DialogPrimitive.Close aria-label="Cerrar" className={cn(closeButton, '-mr-2')}>
              <Icon icon={Cancel01Icon} aria-hidden className="size-[18px]" />
            </DialogPrimitive.Close>
          </div>
          <div className="overflow-y-auto px-6 py-2">{children}</div>
          {footer ? <div className="flex flex-wrap justify-end gap-2 px-6 pb-5 pt-4">{footer}</div> : <div className="pb-4" />}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: React.ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  destructive?: boolean;
  loading?: boolean;
  /** When set, the user must type this exact text before confirming (deliberate destructive actions). */
  requireText?: string;
  onConfirm: () => void;
  error?: React.ReactNode;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel = 'Cancelar',
  destructive,
  loading,
  requireText,
  onConfirm,
  error,
}: ConfirmDialogProps) {
  const [typed, setTyped] = React.useState('');
  const blocked = requireText !== undefined && typed !== requireText;
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) setTyped('');
        onOpenChange(o);
      }}
      title={title}
      description={description}
      size="sm"
      dismissible={!loading}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button variant={destructive ? 'danger' : 'primary'} onClick={onConfirm} disabled={blocked} loading={loading}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="grid gap-3">
        {requireText !== undefined ? (
          <FormField label={`Escribe «${requireText}» para confirmar`}>
            <Input value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" />
          </FormField>
        ) : null}
        {error ? (
          <p role="alert" className="text-meta font-medium text-danger">
            {error}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}

export interface SheetProps extends Omit<DialogProps, 'size' | 'dismissible'> {
  side?: 'left' | 'right';
  /** Tailwind width classes. */
  width?: string;
}

/** Side drawer on the dialog primitive (focus trapped, Escape closes). */
export function Sheet({ open, onOpenChange, title, description, children, footer, trigger, side = 'right', width = 'w-[min(440px,100vw)]', className }: SheetProps) {
  const onCloseAutoFocus = useReturnFocus(trigger ? undefined : open);
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      {trigger ? <DialogPrimitive.Trigger asChild>{trigger}</DialogPrimitive.Trigger> : null}
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-surface-overlay animate-fade-in" />
        <DialogPrimitive.Content
          onCloseAutoFocus={trigger ? undefined : onCloseAutoFocus}
          className={cn(
            'fixed inset-y-0 z-50 flex flex-col border-border-subtle bg-surface-default shadow-overlay',
            side === 'right' ? 'right-0 border-l animate-slide-in-right' : 'left-0 border-r animate-slide-in-left',
            width,
            className,
          )}
        >
          <div className="flex items-start justify-between gap-4 border-b border-border-subtle px-5 py-4">
            <div className="grid gap-1">
              <DialogPrimitive.Title className="text-section font-semibold text-text-primary">{title}</DialogPrimitive.Title>
              {description ? (
                <DialogPrimitive.Description className="text-body text-text-secondary">{description}</DialogPrimitive.Description>
              ) : (
                <DialogPrimitive.Description className="sr-only">{typeof title === 'string' ? title : 'Panel'}</DialogPrimitive.Description>
              )}
            </div>
            <DialogPrimitive.Close aria-label="Cerrar" className={closeButton}>
              <Icon icon={Cancel01Icon} aria-hidden className="size-[18px]" />
            </DialogPrimitive.Close>
          </div>
          <div className="flex-1 overflow-y-auto">{children}</div>
          {footer ? <div className="flex flex-wrap justify-end gap-2 border-t border-border-subtle px-5 py-4">{footer}</div> : null}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
